import { supabase } from '@/api/supabaseClient';

// Callers written against the Base44 SDK read `res.data` on success and
// `err.response.data.error` on failure — that axios shape is load-bearing at
// all seven call sites, so it is preserved here.

class FunctionInvocationError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'FunctionInvocationError';
    this.status = status;
    this.response = { status, data };
  }
}

// Two of the nine Base44 functions became database functions. Routing them
// here keeps `invoke('name', payload)` working at the call sites unchanged.
const RPC_ROUTES = {
  checkCoffeeDuplicate: async ({ place_id, name }) => {
    const { data, error } = await supabase.rpc('check_coffee_duplicate', {
      p_place_id: place_id,
      p_name: name,
    });
    if (error) throw error;
    return data;
  },

  topContributors: async ({ limit = 5 } = {}) => {
    const { data, error } = await supabase.rpc('top_contributors', { p_limit: limit });
    if (error) throw error;
    return { contributors: data ?? [] };
  },

  // Not a Base44 function — it replaces the fetch-all-then-updateMany that
  // EditProfileModal used to run when a user renamed themselves.
  renameMyReviews: async ({ name }) => {
    const { data, error } = await supabase.rpc('rename_my_reviews', { p_name: name });
    if (error) throw error;
    return { updated: data ?? 0 };
  },
};

export async function invoke(name, payload = {}) {
  const route = RPC_ROUTES[name];
  if (route) {
    try {
      return { data: await route(payload) };
    } catch (error) {
      throw new FunctionInvocationError(
        error.message ?? 'Request failed',
        error.status ?? 500,
        { error: error.message ?? 'Request failed' },
      );
    }
  }

  const { data, error } = await supabase.functions.invoke(name, { body: payload });

  if (error) {
    // FunctionsHttpError carries the response; read the body for the message
    // the function actually sent, which is what the UI displays.
    let body = null;
    if (error.context && typeof error.context.json === 'function') {
      body = await error.context.json().catch(() => null);
    }
    const status = error.context?.status ?? error.status ?? 500;
    const message = body?.error ?? error.message ?? 'Request failed';
    throw new FunctionInvocationError(message, status, body ?? { error: message });
  }

  // An Edge Function can answer 200 with an { error } body; treat that as a
  // failure too so callers do not silently render an error object.
  if (data && typeof data === 'object' && data.error && Object.keys(data).length === 1) {
    throw new FunctionInvocationError(data.error, 200, data);
  }

  return { data };
}

export const functions = { invoke };
